import { Component, Inject, OnInit, ChangeDetectorRef } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { filter, switchMap } from 'rxjs/operators';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Store } from '@ngxs/store';
import { AccountState } from 'app/state/account/state';
import { Account } from 'app/core/models/account.types';
import { AccountFoldersService } from 'app/modules/folders/folders.service';
import { FileResource, Document } from 'app/shared/modules/folders/folders.types';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { SetEthanContext } from '@state/ethan/ethan.action';
import { RelatedToType } from '@shared/enums';

@Component({
  selector: 'view-file',
  templateUrl: './view-file.component.html',
  styleUrls: ['./view-file.component.scss'],
})
export class ViewFileComponent extends OnDestroyMixin implements OnInit {
  selectedFile: FileResource;
  selectedDocument: Document;
  documentVersionsDataSource: MatTableDataSource<any>;
  documentVersionsColumns: string[];
  account: Account;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public matDialogRef: MatDialogRef<ViewFileComponent>,
    private accountFoldersService: AccountFoldersService,
    private readonly cdRef: ChangeDetectorRef,
    private confirmService: ConfirmService,
    private readonly store: Store,
  ) {
    super();
    this.documentVersionsDataSource = new MatTableDataSource();
    this.documentVersionsColumns = ['version', 'created_by', 'created_at', 'actions'];
    this.account = this.store.selectSnapshot(AccountState.getAccount);
  }

  ngOnInit(): void {
    this.selectedFile = this.data.fileResource;
    this.setSelectedDocument();
    this.loadFile();
    this.store.dispatch(new SetEthanContext(this.data.fileResource, RelatedToType.FileResource));
  }
  ngOnDestroy(): void {
    this.store.dispatch(new SetEthanContext(null, null));
  }

  private loadFile(): void {
    this.accountFoldersService
      .getFile(this.selectedFile.folder_resource_id, this.selectedFile.id)
      .pipe(untilComponentDestroyed(this))
      .subscribe((fileResource) => {
        this.documentVersionsDataSource.data = fileResource.documents.sort((a, b) => (a.id < b.id ? 1 : -1));
        this.selectedFile = fileResource;
        this.setSelectedDocument();
        this.cdRef.markForCheck();
      });
  }

  setSelectedDocument(document?: Document): void {
    if (document) {
      this.selectedDocument = document;
    } else {
      if (this.selectedFile.documents.length > 0) {
        this.selectedDocument = this.selectedFile.documents[0];
      }
    }
  }

  downloadDocument(document: Document): void {
    window.open(document.original_url, '_blank');
  }

  deleteDocument(document: Document): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() =>
          this.accountFoldersService.deleteDocument(
            this.selectedFile.folder_resource_id,
            this.selectedFile.id,
            document.id,
          ),
        ),
      )
      .subscribe(() => {
        this.loadFile();
      });
  }

  deleteFile(): void {
    this.confirmService
      .openConfirmationModal()
      .pipe(
        untilComponentDestroyed(this),
        filter((res: boolean) => res),
        switchMap(() =>
          this.accountFoldersService.deleteFile(this.selectedFile.folder_resource_id, this.selectedFile.id),
        ),
      )
      .subscribe(() => {
        this.discard();
      });
  }

  onFileChanged(event: any): void {
    const document: File = event.target.files[0];

    this.accountFoldersService
      .uploadDocument(this.selectedFile.folder_resource_id, this.selectedFile.id, document)
      .pipe(untilComponentDestroyed(this))
      .subscribe((doc) => {
        event.target.value = '';
        this.selectedFile = doc;
        this.loadFile();
      });
  }

  discard(): void {
    this.matDialogRef.close();
  }

  trackByFn(index: number, item: any): any {
    return item.id || index;
  }
}
